import { createContext, useState, useContext } from "react";
import useRequestAI, { HandleSendType } from "./hooks/use-request-ai";
import { useWordSuggestionService } from "../word-suggestion";
import { useSettings } from "../settings";
import { Suggestion, SuggestionType } from "../word-suggestion";
import { Message } from "../message/types";
import { RequestStatus } from "../../shared/consts";

type ChatContextValue = {
  messages: Message[];
  input: string;
  setInput: (value: string) => void;
  handleSend: HandleSendType;
  requestStatus: RequestStatus;
  clearChat: () => void;
  getQuestion: () => Promise<void>;
  suggestions: Suggestion[];
  setSuggestions: (suggestions: Suggestion[]) => void;
  inputKeywords: string[];
  setInputKeywords: (keywords: string[]) => void;
  handleSuggestionClick: (suggestion: Suggestion) => void;
  requestReportStatus: RequestStatus;
  displayAllSuggestions: boolean;
  setDisplayAllSuggestions: (value: boolean) => void;
  handleUserInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  feToTypeDict: Record<string, SuggestionType | undefined>;
  lastWord: string;
};

type ChatContextProviderProps = {
  children: React.ReactNode;
};

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

function ChatContextProvider({ children }: ChatContextProviderProps) {
  const [input, setInput] = useState("");
  const { chatSettings } = useSettings();

  const {
    suggestions,
    setSuggestions,
    inputKeywords,
    setInputKeywords,
    handleSuggestionClick,
    displayAllSuggestions,
    setDisplayAllSuggestions,
    resetWordSuggestions,
    handleUserInputChange,
    feToBeTermsDict,
    feToTypeDict,
    lastWord,
  } = useWordSuggestionService(input, setInput);

  const {
    messages,
    handleSend,
    requestStatus,
    clearChat,
    getQuestion,
    requestReportStatus,
  } = useRequestAI({
    input,
    setInput,
    resetWordSuggestions,
    feToBeTermsDict,
    chatSettings,
  });
  return (
    <ChatContext.Provider
      value={{
        messages,
        input,
        setInput,
        handleSend,
        requestStatus,
        clearChat,
        getQuestion,
        suggestions,
        setSuggestions,
        inputKeywords,
        setInputKeywords,
        handleSuggestionClick,
        requestReportStatus,
        displayAllSuggestions,
        setDisplayAllSuggestions,
        handleUserInputChange,
        feToTypeDict,
        lastWord,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

const useChatContext = (): ChatContextValue => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatContextProvider");
  }
  return context;
};

export { ChatContext, ChatContextProvider, useChatContext };
